import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { appApi, authApi, type AppSettings, type ApiError } from "@/api";

/**
 * App-level state used by the router guard:
 *
 *   - `configured`:   has the instance been bootstrapped with a password?
 *   - `authenticated`: does the current cookie validate?
 *   - `settings`:      the public settings map (mirrors the backend)
 *
 * The router awaits `bootstrap()` once on first navigation so the guard can
 * decide between /, /login, and /settings without races.
 */
export const useAppStore = defineStore("app", () => {
    const configured = ref<boolean | null>(null);
    const authenticated = ref<boolean | null>(null);
    const settings = ref<Record<string, string>>({});
    const loading = ref(false);
    const error = ref<string | null>(null);

    let bootPromise: Promise<void> | null = null;

    const bootstrapped = computed(() => configured.value !== null);

    async function bootstrap(): Promise<void> {
        if (bootPromise) return bootPromise;
        bootPromise = (async () => {
            loading.value = true;
            try {
                const s = await appApi.getSettings();
                configured.value = s.configured;
                settings.value = s.settings ?? {};
                if (s.configured) {
                    try {
                        await authApi.me();
                        authenticated.value = true;
                    } catch (err) {
                        if ((err as ApiError | undefined)?.status === 401) {
                            authenticated.value = false;
                        } else {
                            authenticated.value = false;
                            error.value = (err as Error).message;
                        }
                    }
                } else {
                    authenticated.value = false;
                }
            } catch (err) {
                error.value = (err as Error).message;
                // Leave configured = null so the UI can show a connection error.
            } finally {
                loading.value = false;
            }
        })();
        return bootPromise;
    }

    /** Force a re-fetch of /settings (e.g. after a successful PATCH). */
    async function refresh(): Promise<void> {
        bootPromise = null;
        await bootstrap();
    }

    async function login(password: string): Promise<void> {
        await authApi.login(password);
        authenticated.value = true;
    }

    async function logout(): Promise<void> {
        try { await authApi.logout(); } catch { /* ignore */ }
        authenticated.value = false;
    }

    /**
     * Save settings via PATCH. When the very-first call includes
     * `auth.password`, the backend Set-Cookies us in — flip `configured` and
     * `authenticated` locally to match.
     */
    async function saveSettings(updates: Record<string, string>): Promise<void> {
        const wasFirstRun = configured.value === false;
        const next = await appApi.patchSettings(updates);
        configured.value = next.configured;
        settings.value = next.settings ?? {};
        if (wasFirstRun && next.configured) {
            authenticated.value = true;
        }
    }

    return {
        configured,
        authenticated,
        settings,
        loading,
        error,
        bootstrapped,
        bootstrap,
        refresh,
        login,
        logout,
        saveSettings,
    };
});
